import type { ReactNode } from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AppHeaderProps as SharedAppHeaderProps } from '@sudoku/shared';
import { ThemeToggle } from '../theme/ThemeToggle';
import { FontSizeToggle } from './FontSizeToggle';
import { LangToggle } from './LangToggle';
import { AuthBtn } from './AuthBtn';
import { CoinChip } from './CoinChip';
import { TrophyChip } from './TrophyChip';
import { useI18n } from '../i18n';
import { track } from '../lib/analytics';
import { useAuth } from '../auth';
import { useOnlineStatus } from '../hooks/useOnlineStatus';

/** Web-specific AppHeaderProps: adds showAuth control, narrows ReactNode fields */
interface AppHeaderProps extends Omit<SharedAppHeaderProps, 'centerContent' | 'extraControls'> {
  showAuth?: boolean;
  centerContent?: ReactNode;
  extraControls?: ReactNode;
  /** Show the font-size (Aa) toggle — only on game screens */
  showFontToggle?: boolean;
  /** Show home icon button next to back arrow — default false */
  showHomeBtn?: boolean;
}

export function AppHeader({ onBack, backLabel, title, centerContent, extraControls, showAuth = true, showFontToggle = false, showHomeBtn = false }: AppHeaderProps) {
  const { online } = useOnlineStatus();
  const { t } = useI18n();
  const navigate = useNavigate();
  const { user, sendVerificationEmail } = useAuth();
  const [verifyState, setVerifyState] = useState<'idle' | 'sending' | 'sent'>('idle');
  const needsVerify = user && !(user as any).is_verified;

  const handleSendVerify = async () => {
    if (verifyState !== 'idle') return;
    setVerifyState('sending');
    try {
      await sendVerificationEmail();
      setVerifyState('sent');
    } catch {
      setVerifyState('idle');
    }
  };
  return (
    <>
    <header className="app-header">
      {/* SOL: geri oku yoksa site adı (tıklanabilir), varsa geri oku + ev ikonu + offline badge */}
      <div className="app-header-left">
        {onBack ? (
          <>
            <button
              className="app-header-back"
              onClick={onBack}
              title={backLabel ?? t.common.backToHome}
              aria-label={backLabel ?? t.common.backToHome}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            {showHomeBtn && (
              <button
                className="app-header-home-btn"
                onClick={() => navigate('/')}
                title={t.common.backToHome}
                aria-label={t.common.backToHome}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V9.5z"/>
                  <polyline points="9 21 9 12 15 12 15 21"/>
                </svg>
              </button>
            )}
          </>
        ) : (
          <div className="app-header-store-links">
            <a
              href="https://apps.apple.com/tr/app/sudoku-area/id6768587148?l=tr"
              target="_blank"
              rel="noopener noreferrer"
              className="app-header-store-btn"
              aria-label={t.common.downloadAppStore}
              onClick={() => track('store_click', { store: 'ios', placement: 'header' })}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.7 9.05 7.42c1.27.07 2.14.82 2.88.84.85 0 2.45-.98 4.12-.83 1.74.17 2.96.89 3.79 2.02-3.41 2.04-2.85 6.52.57 7.89-.45 1.08-.93 2.14-1.36 2.94zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
              </svg>
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=com.sudokuarea.app"
              target="_blank"
              rel="noopener noreferrer"
              className="app-header-store-btn"
              aria-label={t.common.downloadGooglePlay}
              onClick={() => track('store_click', { store: 'android', placement: 'header' })}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M3 1.6v20.8L13.8 12z" fill="#4285F4"/>
                <path d="M13.8 12L3 1.6l15.1 8.7z" fill="#EA4335"/>
                <path d="M13.8 12L3 22.4l15.1-8.7z" fill="#34A853"/>
                <path d="M18.1 10.3L13.8 12l4.3 1.7L21 12z" fill="#FBBC04"/>
              </svg>
            </a>
          </div>
        )}
        {!online && (
          <span
            className="offline-badge"
            role="status"
            aria-live="polite"
            aria-label={t.offline.badge}
          >
            {t.offline.badge}
          </span>
        )}
      </div>

      {/* ORTA: özel içerik (oda kodu, başlık vb.) — sadece prop verilirse görünür */}
      <div className="app-header-center">
        {centerContent}
      </div>

      {/* SAĞ: tüm kontroller sıkı cluster — ⚙️ · 🌙 · Aa · 🌐 · Profil */}
      <div className="app-header-right">
        {extraControls}
        <CoinChip />
        <TrophyChip />
        <ThemeToggle />
        {showFontToggle && <FontSizeToggle />}
        <LangToggle />
        {showAuth && <AuthBtn />}
      </div>
    </header>
    {title && <h2 className="page-title">{title}</h2>}
    {needsVerify && (
      <div className="verify-email-bar">
        <span>📬 {t.profile.verifyEmailNotice}</span>
        <button className="verify-email-bar-btn" onClick={handleSendVerify} disabled={verifyState !== 'idle'}>
          {verifyState === 'sent' ? t.profile.verifyEmailSent : verifyState === 'sending' ? '...' : t.profile.verifyEmailResend}
        </button>
      </div>
    )}
    </>
  );
}
